import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  OutlinedInput,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Spinner from 'src/components/Spinner/Spinner';
import HelpCircleMUI6 from 'src/components/HelpCircleMUI6';
import InputStartAdornment from 'src/views/management/ChatView/NewMessage/InputStartAdornment';
import InputEndAdornment from 'src/views/management/ChatView/NewMessage/InputEndAdornment';
import AttachedFilePrewiew from 'src/views/management/ChatView/NewMessage/AttachedFilePrewiew';
import TemplateAndDynamicFieldSelector from 'src/views/management/ChatView/modals/TemplateAndDynamicFieldSelector';
import {
  sendOrEditScheduledMessage,
  setCurrentMessage,
  setFileUploadToMessage,
  getFollowUpMessages,
  clearFollowUpMessages,
} from 'src/actions/chatActions';
import { addNotification } from 'src/actions/notificationActions';
import helpers from 'src/helpers';
import {
  NOTIFICATION_STATUS,
  STORAGE,
  STYLE_OPTIONS,
} from 'src/constants';

const isMessageEdit = true;

const VALIDATE_MESSAGE = {
  validateFileSize: 'File size must be less than 1.4Mb',
  validateFileType: 'Must be a jpg, jpeg, png, or gif file',
};

function ModalEditScheduledMessage({
  isModalVisible,
  chatKey,
  onCloseModal,
}) {
  const [messageCursorPosition, setMessageCursorPosition] = useState(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [insertMode, setInsertMode] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, azAccountName, databaseBeingUsed } = useSelector((state) => state?.account || {});
  const {
    currentMessage,
    followUpMessages,
    isMessageSending,
    isMessageUpdating,
  } = useSelector((state) => state?.chat || {});
  const {
    id,
    scheduledAt,
    body,
    attachment,
    messageAttachmentContainer,
    fileToReplace = false,
  } = currentMessage || {};
  const { dynamicFields } = user || {};
  const {
    followUpMessageList,
    loading: isFollowUpMessagesLoading,
  } = followUpMessages || {};
  const dispatch = useDispatch();

  const initialValues = {
    messageBody: body || '',
    attachment: attachment || '',
    messageAttachmentContainer: messageAttachmentContainer || null,
  };

  const validationSchema = Yup.object().shape({
    messageBody: Yup.string(),
    attachment: Yup.string()
      .test('invalidAttachmentSize', VALIDATE_MESSAGE.validateFileSize, () => helpers.azure.validateFileSize(fileToReplace))
      .test('invalidAttachmentType', VALIDATE_MESSAGE.validateFileType, () => helpers.azure.validateFileType(fileToReplace)),
  });

  const fileWasNotUpload = () => {
    dispatch(addNotification('file upload', 'Your file wasn\'t uploaded', NOTIFICATION_STATUS.ERROR));
  };

  const fileWasNotDelete = () => {
    dispatch(addNotification('file delete', 'Your file wasn\'t deleted', NOTIFICATION_STATUS.ERROR));
  };

  const handleClose = () => {
    onCloseModal();
    setInsertMode('');
    dispatch(setCurrentMessage({}));
  };

  const onSubmit = async (values, {
    resetForm, setStatus, setSubmitting
  }) => {
    setSubmitting(true);
    const doWhenTry = () => {
      resetForm();
      setStatus({ success: true });
      setSubmitting(false);
      handleClose();
    };
    const doWhenCatch = () => {
      setStatus({ success: false });
      setSubmitting(false);
    };

    // Determining the attachment container based on file changes:
    // - If the file was deleted (fileToReplace === null), set container to null.
    // - If a new file was attached (fileToReplace is an object), generate a new container name.
    // - Otherwise, keep the existing container.
    const attachmentContainerToSend = fileToReplace === null
      ? null
      : fileToReplace
        ? helpers.azure.getUserContainerName(databaseBeingUsed, user.id)
        : values.messageAttachmentContainer;

    const newValuesAttachment = await helpers.azure.loadFileToAzureAndGetStatusCode(fileToReplace, attachment, user.id, azAccountName, STORAGE.sas, databaseBeingUsed, attachmentContainerToSend, fileWasNotUpload, fileWasNotDelete, false, 'EO-ID', false);
    if (newValuesAttachment === null) {
      dispatch(addNotification('saveFile', 'There are some problems with Azure service.', NOTIFICATION_STATUS.ERROR));
      return;
    }
    if (helpers.isEmpty(values.messageBody) && (!newValuesAttachment || helpers.isEmpty(newValuesAttachment))) {
      dispatch(addNotification('sendFile', 'You must enter either a text message or select an image.', NOTIFICATION_STATUS.ERROR));
      return;
    }
    const attachmentFromMessage = !helpers.isEmpty(values.attachment) && fileToReplace !== null ? values.attachment : null;
    const attachmentToSend = !helpers.isEmpty(newValuesAttachment) ? newValuesAttachment : attachmentFromMessage;

    const sendData = {
      id,
      scheduledAt,
      body: values.messageBody?.trim() ? values.messageBody : null,
      attachment: attachmentToSend,
      messageAttachmentContainer: attachmentContainerToSend
    };
    dispatch(sendOrEditScheduledMessage(sendData, chatKey, doWhenTry, doWhenCatch));
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });
  const {
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    isSubmitting,
    values,
  } = formik;

  const isLoading = isSubmitting || isMessageSending || isMessageUpdating || isFollowUpMessagesLoading;
  const isMessageIncludesBlanks = values?.messageBody && values.messageBody?.includes('__________');

  const saveMessageCursorPosition = (event) => {
    setMessageCursorPosition(event?.target?.selectionStart);
  };

  const handleOpenPopover = (event) => {
    setPopoverAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setPopoverAnchorEl(null);
  };

  const handleOpenDynamicFieldSelect = () => {
    if (insertMode === 'dynamicFields') {
      setInsertMode('');
    } else {
      setInsertMode('dynamicFields');
    }
    handleClosePopover();
  };

  const handleOpenTemplateSelect = () => {
    if (insertMode === 'templates') {
      setInsertMode('');
    } else {
      setInsertMode('templates');
    }
    handleClosePopover();
  };

  const { getInputProps, open: handleOpenAttachFile } = useDropzone({
    maxFiles: 1,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    noDrag: true,
    accept: {
      'image/jpeg': ['.jpeg', '.png']
    },
  });

  const updateFileUploadToMessage = (value) => {
    dispatch(setFileUploadToMessage(value, isMessageEdit));
    setFieldValue('attachment', '', false);
    setFieldValue('messageAttachmentContainer', null, false);
  };

  const validateFileInput = (uploadFileData) => {
    if (!helpers.azure.validateFileSize(uploadFileData)) {
      dispatch(addNotification('attachment-size', VALIDATE_MESSAGE.validateFileSize, NOTIFICATION_STATUS.ERROR));
      return false;
    }
    if (!helpers.azure.validateFileType(uploadFileData)) {
      dispatch(addNotification('attachment-type', VALIDATE_MESSAGE.validateFileType, NOTIFICATION_STATUS.ERROR));
      return false;
    }
    return true;
  };

  const handleOnSelectedFile = async (event) => {
    const { target: { files, value } } = event;
    if (!files || !value) {
      return;
    }
    const uploadFileData = {
      file: files[0],
      value
    };
    if (validateFileInput(uploadFileData)) {
      dispatch(setFileUploadToMessage(uploadFileData, isMessageEdit));
    }
    handleClosePopover();
  };

  const onBlurInput = (event) => {
    handleBlur(event);
    saveMessageCursorPosition(event);
  };

  const handleSetFocusField = useCallback(() => {
    const inputElements = document.getElementsByName('messageBody');
    if (inputElements.length > 0) {
      inputElements[0].focus(); // {preventScroll: true} doesn't work

      if (typeof messageCursorPosition === 'number') {
        // Set the cursor position for input
        inputElements[0].setSelectionRange(messageCursorPosition, messageCursorPosition);
      }
    }
  }, [messageCursorPosition]);

  const handleAddEmoji = (e) => {
    const emoji = e.native;
    const currentText = values.messageBody;
    // true when messageCursorPosition isn't number maybe false or null
    const isNullMessageCursorPosition = !messageCursorPosition && messageCursorPosition !== 0;
    const text = isNullMessageCursorPosition
      ? `${currentText}${emoji}`
      : `${currentText.substring(0, messageCursorPosition)}${emoji}${currentText.substring(messageCursorPosition)}`;
    const length = !isNullMessageCursorPosition ? messageCursorPosition + emoji.length : text.length;
    setFieldTouched('messageBody', true);
    setFieldValue('messageBody', text, true);
    setMessageCursorPosition(length);
  };

  useEffect(() => {
    if (isModalVisible) {
      dispatch(getFollowUpMessages());
    }

    return () => {
      dispatch(clearFollowUpMessages());
    };
  }, [dispatch, isModalVisible]);

  return (
    <Dialog
      open={isModalVisible}
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          padding: isMobile ? '24px 0' : '24px',
        },
      }}
    >
      <Box
        sx={{
          padding: '0 0 0 16px',
          textAlign: 'center',
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '18px',
            padding: 0,
          }}
        >
          Update Message
        </DialogTitle>
        <Typography
          variant="subtitle1"
          color="#546E7A"
        >
          Update this scheduled message
        </Typography>
      </Box>
      <DialogContent>
        <form
          id="formEditScheduledMessage"
          onSubmit={handleSubmit}
        >
          <Spinner
            loading={isLoading}
            positionType="absolute"
          />
          {insertMode && (
            <TemplateAndDynamicFieldSelector
              insertMode={insertMode}
              dynamicFields={dynamicFields}
              templates={followUpMessageList}
              messageCursorPosition={messageCursorPosition}
              setMessageCursorPosition={setMessageCursorPosition}
              formik={formik}
            />
          )}
          {(fileToReplace || values.attachment) && (
            <AttachedFilePrewiew
              fileName={helpers.azure.getImageFileURL(values.attachment, user.id, azAccountName, STORAGE.sas, databaseBeingUsed, values.messageAttachmentContainer)}
              fileUpload={fileToReplace}
              isLoading={isLoading}
              validateFileInput={validateFileInput}
              setFileUploadToMessage={updateFileUploadToMessage}
            />
          )}
          {isMessageIncludesBlanks && (
            <Box
              sx={{
                display: 'flex',
                marginTop: '8px',
                color: '#F15642',
              }}
            >
              <Typography
                variant="h5"
              >
                Please review!&nbsp;&nbsp;&nbsp;Includes blanks!
              </Typography>
              <HelpCircleMUI6
                showInfo={(
                  <>
                    This may have happened because you used a template that has dynamic field(s) that cannot be used on this screen.
                    <br />
                    <br />
                    Review the blanks & fill them in, or use a different template.
                  </>
                )}
              />
            </Box>
          )}
          <OutlinedInput
            name="messageBody"
            color="secondary"
            placeholder="Write something..."
            onChange={handleChange}
            onBlur={onBlurInput}
            value={values.messageBody}
            disabled={isLoading}
            size="small"
            minRows={8}
            maxRows={16}
            startAdornment={(
              <InputStartAdornment
                isLoading={isLoading}
                handleOpenPopover={handleOpenPopover}
                handleClosePopover={handleClosePopover}
                popoverAnchorEl={popoverAnchorEl}
                openUploadDialog={handleOpenAttachFile}
                openDynamicFieldsDialog={handleOpenDynamicFieldSelect}
                openMessageTemplateDialog={handleOpenTemplateSelect}
              />
            )}
            endAdornment={!isMobile && (
              <InputEndAdornment
                isLoading={isLoading}
                handleAddEmoji={handleAddEmoji}
                handleSetFocusField={handleSetFocusField}
              />
            )}
            sx={{
              marginTop: '8px',
              paddingLeft: '8px',
              backgroundColor: isMessageIncludesBlanks ? '#f0f0f0' : undefined
            }}
            fullWidth
            multiline
          />
          <input {...getInputProps({ onChange: handleOnSelectedFile })} />
        </form>
      </DialogContent>
      <DialogActions
        sx={{
          padding: 0,
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            ...STYLE_OPTIONS.BUTTON_PRIMARY_REVERSED,
            minWidth: '105px',
            marginRight: '14px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={(!values.messageBody?.trim() && !fileToReplace && !values.attachment) || isLoading}
          sx={{
            ...STYLE_OPTIONS.BUTTON_PRIMARY,
            minWidth: '105px',
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ModalEditScheduledMessage.propTypes = {
  isModalVisible: PropTypes.bool.isRequired,
  chatKey: PropTypes.string.isRequired,
  onCloseModal: PropTypes.func.isRequired,
};

ModalEditScheduledMessage.defaultProps = {};

export default ModalEditScheduledMessage;
